import PixDiscount from '$store/components/product/PixDiscount.tsx'
import Icon from '$store/components/ui/Icon.tsx'
import Text from '$store/components/ui/Text.tsx'
import AddToCartButton from '$store/islands/AddToCartButton.tsx'
import { formatPrice } from '$store/sdk/format.ts'
import { Options } from '$store/sdk/useAddToCart.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import { useVariantPossibilities } from '$store/sdk/useVariantPossibilities.ts'
import { useSignal } from '@preact/signals'
import type { Product as TypeProduct } from 'apps/commerce/types.ts'
import Image from 'apps/website/components/Image.tsx'
import Modal from 'site/components/ui/Modal.tsx'
import RenderHTML from 'site/components/ui/RenderHTML.tsx'
import { assert } from 'site/sdk/assert.ts'
import c from 'site/sdk/c.ts'
import { useId } from 'site/sdk/useId.ts'

export interface Props {
    parts: TypeProduct[]
    name: string
    gtin: TypeProduct['gtin']
    layoutVariant?: 'details' | 'quick-view'
    id?: string
}

interface VariantSelectorProps {
    product: TypeProduct
    selectedSkuId: string | null
    onSelect: (skuId: string) => void
    layoutVariant: Props['layoutVariant']
}

function VariantSelector({
    product,
    selectedSkuId,
    onSelect,
    layoutVariant,
}: VariantSelectorProps) {
    const possibilities = useVariantPossibilities(product)
    if (!possibilities.Tamanho || Object.keys(possibilities.Tamanho).length === 0) {
        return null
    }

    const { url: currentUrl } = product

    return (
        <div id='sku-selector'>
            <Text class='text-neutral-0 mb-1.5 block font-bold font-lato text-sm'>
                Selecione o tamanho
            </Text>
            <ul
                class={c(
                    'flex items-center flex-wrap',
                    layoutVariant === 'details' ? 'gap-1' : 'gap-2',
                )}
            >
                {Object.entries(possibilities.Tamanho).map(([value, { urls, isAvailable }]) => {
                    const url = urls.find((url) => url === currentUrl) || urls[0]
                    const skuId = new URL(url).searchParams.get('skuId')

                    const isSelected = selectedSkuId === skuId

                    return (
                        <li>
                            <button
                                type='button'
                                data-is-available={isAvailable}
                                data-is-selected={isSelected}
                                href={url}
                                onClick={() => onSelect(assert.unreachable(skuId))}
                                disabled={!isAvailable}
                                class={c(
                                    'grid relative disabled:text-neutral-3 disabled:hover:text-neutral-3 text-neutral-0 bg-grey-100 place-items-center text-sm',
                                    !isAvailable &&
                                        layoutVariant === 'details' &&
                                        'overflow-hidden after:absolute after:bg-neutral-3 after:w-[150%] after:h-px after:-rotate-45 after:origin-right after:top-0 after:right-0',
                                    layoutVariant === 'details'
                                        ? 'w-9 h-9 border'
                                        : 'w-10 h-8 rounded-md',
                                    layoutVariant === 'details' && isAvailable
                                        ? 'border-neutral-4'
                                        : 'border-neutral-3',
                                    selectedSkuId === null && 'hover:bg-grey-100',
                                    selectedSkuId === null &&
                                        !isAvailable &&
                                        'hover:border-neutral-1',
                                    isAvailable &&
                                        'hover:bg-primary-700 hover:text-white transition-colors cursor-pointer',
                                    isSelected && 'bg-primary-700 text-white',
                                )}
                            >
                                {value}
                                {!isAvailable && layoutVariant !== 'details' && (
                                    <div class='p-[2px] rounded-sm bg-grey-500 absolute -top-1 -right-1'>
                                        <Icon id='XMark' height={8} width={8} class='text-white' />
                                    </div>
                                )}
                            </button>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

interface KitLookProductProps extends TypeProduct, ReturnType<typeof useOffer> {
    selectedSkuId: string | null
    onSelect: (skuId: string | null) => void
}

function Product(product: KitLookProductProps & { layoutVariant: Props['layoutVariant'] }) {
    const {
        image,
        alternateName,
        isVariantOf,
        onSelect,
        selectedSkuId,
        price,
        listPrice,
        url,
        bestInstallment: installment,
        description,

        layoutVariant,
    } = product

    const alert = useSignal<string | null>(null)
    const id = useId()

    const onSelectClick = () => {
        if (selectedSkuId) {
            onSelect(null)
        } else {
            alert.value = 'Selecione um tamanho'
        }
    }

    const onSkuSelection = (skuId: string) => {
        onSelect(skuId)
        alert.value = null
    }

    return (
        <div
            class='grid gap-x-5 lg:gap-x-4 gap-y-4 md:gap-y-0 border-b border-b-grey-250 last:border-none py-4 group grid-cols-[24px_108px_1fr] md:grid-rows-[100px_1fr]'
            data-selected={!!selectedSkuId}
        >
            <button
                type='button'
                class='h-6 w-6 border border-[#D7D7D7] grid place-items-center group-data-[selected=true]:border-none md:row-span-2'
                aria-label='Selecionar'
                onClick={onSelectClick}
            >
                <Icon
                    id='Checked'
                    class='text-primary-400 group-data-[selected=true]:block hidden transition-colors'
                    height={24}
                    width={24}
                    strokeWidth={1}
                />
            </button>
            <a
                href={url?.split('?')[0]}
                class='md:row-span-2'
                aria-label={alternateName ? alternateName : 'produto do kit look'}
            >
                <Image
                    src={assert.unreachable(image?.[0].url)}
                    alt={alternateName ? alternateName : 'produto do kit look'}
                    height={136}
                    width={108}
                    class='h-fit'
                />
            </a>
            <div class='flex flex-col gap-[10px] pr-2 md:pr-0'>
                <h2 class='text-sm font-lato text-neutral-0 font-bold line-clamp-2 min-h-[32px]'>
                    {isVariantOf?.name}
                </h2>

                <div class='flex flex-col mb-1 text-s'>
                    <div class='flex items-center gap-x-2 flex-row'>
                        {listPrice && price && listPrice > price && (
                            <div class='text-light text-xs font-lato text-neutral-2 line-through'>
                                {formatPrice(listPrice)}
                            </div>
                        )}
                        <div class='font-extrabold font-lato text-sm text-primary-700'>
                            {price && formatPrice(price)}
                        </div>
                    </div>

                    {layoutVariant === 'details'
                        ? (
                            <>
                                <label
                                    for={`description:${id}`}
                                    class='text-neutral-1 text-xs font-bold underline underline-offset-2 block mt-1 cursor-pointer self-start'
                                >
                                    Ver descrição do produto
                                </label>
                                <Modal id={`description:${id}`}>
                                    <div class='absolute p-10 bg-white flex flex-col gap-y-3 w-full max-w-[700px] h-full min-[440px]:max-h-[70vh] overflow-y-auto'>
                                        <label
                                            for={`description:${id}`}
                                            class='absolute top-4 right-4 cursor-pointer text-neutral-0'
                                        >
                                            <Icon id='XMark' width={32} height={32} />
                                        </label>
                                        <span class='text-lg text-neutral-1 font-bold'>
                                            Descrição do Produto
                                        </span>
                                        <RenderHTML html={description ?? ''} />
                                    </div>
                                </Modal>
                                <div class='flex-grow' />
                            </>
                        )
                        : (
                            <>
                                {installment?.billingIncrement &&
                                    installment.billingDuration &&
                                    installment.billingDuration > 1 && (
                                    <div class='text-neutral-1 flex items-center gap-1 text-xs'>
                                        <Icon id='Card' width={15} height={11} class='shrink-0' />
                                        <span class='font-semibold'>Ou</span>{' '}
                                        <strong>{installment.billingDuration}x</strong> de{' '}
                                        <strong>{formatPrice(installment.billingIncrement)}</strong>
                                    </div>
                                )}
                            </>
                        )}
                </div>
            </div>
            <div class='col-start-2 col-span-2 row-start-2 md:col-start-3 md:col-span-1'>
                <VariantSelector
                    product={product}
                    selectedSkuId={selectedSkuId}
                    onSelect={onSkuSelection}
                    layoutVariant={layoutVariant}
                />
                {alert.value && (
                    <div class='bg-danger-700 w-full mt-[15px] text-center py-1'>
                        <span class='text-xs block font-lato text-white'>{alert.value}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

function KitLookDetails({ parts, layoutVariant = 'details', id }: Props) {
    const alert = useSignal<string | null>(null)

    const onSelect = (skuId: string | null, index: number) => {
        products.value[index].selectedSkuId = skuId
        products.value = [...products.value]
        alert.value = null
    }

    const products = useSignal<KitLookProductProps[]>(
        parts.map((part, index) => {
            return {
                ...part,
                selectedSkuId: null,
                onSelect: (skuId: string | null) => onSelect(skuId, index),
                ...useOffer(part.offers),
            }
        }),
    )

    const selectedProducts = products.value.filter((product) => product.selectedSkuId)

    const total = selectedProducts.reduce((total, product) => {
        return total + (product.price || 0)
    }, 0)

    const itemsToAdd = selectedProducts.map((product) => {
        const { productID, price, listPrice, seller, url, selectedSkuId } = product

        return {
            skuId: selectedSkuId,
            brand: product.brand ?? '',
            category: product.category ?? '',
            productGroupID: product.isVariantOf?.productGroupID,
            productGroupName: product.isVariantOf?.name,
            listPrice: listPrice ?? price ?? 0,
            productID: productID,
            url: url ?? '',
            sellerId: seller ?? '1',
            price: price ?? 0,
            discount: price && listPrice ? listPrice - price : 0,
            name: product.name ?? '',
            productGroupId: product.isVariantOf?.productGroupID ?? '',
        } as Options['itemsToAdd']['0']
    })

    return (
        <div class='flex flex-col h-full max-w-[477px] group relative mt-2'>
            <div
                class={c(
                    'overflow-auto scrollbar-thin-rounded md:pr-8 mb-4',
                    layoutVariant === 'details' ? 'lg:max-h-[700px]' : 'lg:max-h-[400px]',
                )}
            >
                {products.value.map((product) => (
                    <Product {...product} layoutVariant={layoutVariant} />
                ))}
            </div>
            {layoutVariant === 'details' && (
                <>
                    <div class='flex flex-col lg:flex-row items-center justify-between gap-y-2 gap-x-5 bg-grey-100 border-t border-t-grey-250 p-5 rounded-[10px] text-neutral-0'>
                        <div>
                            <p class='text-sm'>Produtos selecionados: {selectedProducts.length}</p>
                            <p class='text-sm items-end gap-1'>
                                Total:{' '}
                                <strong class='font-bold text-primary-700 text-base'>
                                    {formatPrice(total)}
                                </strong>
                            </p>
                        </div>
                        {
                            /* <div class='lg:h-4 mb-3'>
                        {total > 0 && <PixDiscount discountPercentage={3} price={total} />}
                    </div> */
                        }

                        <AddToCartButton
                            variant='kitLook'
                            onClickWithoutProducts={() => {
                                alert.value = 'Selecione ao menos um produto'
                            }}
                            itemsToAdd={itemsToAdd}
                            class='h-[41px] max-w-[251px] bg-success-700 hover:bg-success-600 z-20'
                        >
                            Comprar o Look
                        </AddToCartButton>
                    </div>
                    {alert.value && (
                        <div class='bg-danger-700 w-full mt-2 text-center py-1'>
                            <span class='text-xs block font-lato text-white'>{alert.value}</span>
                        </div>
                    )}
                </>
            )}
            {layoutVariant === 'quick-view' && (
                <div>
                    {alert.value && (
                        <div class='bg-danger-700 w-full text-center py-1'>
                            <span class='text-xs block font-lato text-white'>{alert.value}</span>
                        </div>
                    )}
                    <div class='flex items-center w-full pt-5 bg-white'>
                        <div class='flex-1'>
                            <p class='text-sm text-neutral-0 font-lato '>
                                Produtos selecionados: {selectedProducts.length}
                            </p>
                            <p class='text-sm text-neutral-0 font-lato flex items-end gap-3'>
                                Total:{' '}
                                <strong class='font-bold font-lato text-primary-700'>
                                    {formatPrice(total) || 'R$ 0,00'}
                                </strong>
                            </p>
                        </div>

                        <AddToCartButton
                            variant='kitLook'
                            onClickWithoutProducts={() => {
                                alert.value = 'Selecione ao menos um produto'
                            }}
                            itemsToAdd={itemsToAdd}
                            class='h-[41px] max-w-[251px] bg-success-700 hover:bg-success-600 z-20 flex-1'
                            onCloseModal={() => {
                                if (id) {
                                    document.getElementById(id)?.click()
                                }
                            }}
                        >
                            Comprar o Look
                        </AddToCartButton>
                    </div>
                </div>
            )}
        </div>
    )
}

export default KitLookDetails
